import * as React from "react";
import { RecommendationKind } from "../App";
import {
	doCardLogging,
	logRecommendationAvailability,
} from "../Helper/logging";
import SliderHelper from "../Helper/SliderHelper";
import Arrow, { ArrowProps, Orientation } from "./Arrow";
import { CardType } from "./Card";
import CSHPQnACard from "./CSHPQnACard";
import CSHPTBSCard, { TBSRecommendation } from "./CSHPTBSCard";
import { QnARecommendation } from "./QnACard";
import { RecommendationItem, RecommendationList } from "./Recommendation";
import SliderError from "./SliderError";
import SliderLoading from "./SliderLoading";
import { debounce } from "ts-debounce";

export enum ArrowState {
	NoArrow = "NoArrow",
	PeekLeft = "PeekLeft",
	PeekRight = "PeekRight",
	PeekLeftAndRight = "PeekLeftAndRight",
}

export enum SliderMode {
	Slider = "Slider",
	Scroll = "Scroll",
}

interface SliderState {
	arrowState: ArrowState;
	translateX: number;
	sliderMode: SliderMode;
}

export interface SliderProps {
	error?: boolean;
	data?: RecommendationList;
	reload?: () => void;
}

export interface RecommendationSet {
	id: number;
	items: RecommendationItem[];
}

class Slider extends React.Component<SliderProps, SliderState> {
	static displayName = "Slider";
	sliderRootRef: React.RefObject<HTMLDivElement>;
	sliderHelper: SliderHelper;
	scrolling: boolean;
	_debouncedResize: any;
	_debouncedScroll: any;

	constructor(props: SliderProps) {
		super(props);
		this.scrolling = false;
		this.sliderRootRef = React.createRef<HTMLDivElement>();
		this._debouncedResize = debounce(this.resize.bind(this), 200);
		this._debouncedScroll = debounce(this.scroll.bind(this), 50);

		this.state = {
			arrowState: ArrowState.NoArrow,
			translateX: 0,
			sliderMode: SliderMode.Scroll,
		};
	}

	componentWillUnmount() {
		if (typeof window !== "undefined") {
			window.removeEventListener("resize", this._debouncedResize);
		}
		if (this.sliderHelper.sliderViewport) {
			this.sliderHelper.sliderViewport.removeEventListener(
				"scroll",
				this._debouncedScroll
			);
		}
	}

	render() {
		const { props, state } = this;

		const isLoading =
			(!props.data && !props.error) || (props.data && !props.data.set);

		const noRecommendationsFound =
			this.props.data &&
			this.props.data.set &&
			this.props.data.set.items &&
			this.props.data.set.items.length === 0;

		let GenericCard: (props: any) => JSX.Element;

		if (props.data) {
			switch (props.data.kind) {
				case RecommendationKind.TBS:
					GenericCard = CSHPTBSCard;
					break;
				case RecommendationKind.QnA:
					GenericCard = CSHPQnACard;
					break;
			}
		}

		const leftArrowProps: ArrowProps = {
			visible: false,
			display: true,
			orientation: Orientation.left,
			onClick: e => this.moveLeft(e),
		};

		const rightArrowProps: ArrowProps = {
			visible: false,
			display: true,
			orientation: Orientation.right,
			onClick: e => this.moveRight(e),
		};

		if (!props.error && !noRecommendationsFound) {
			switch (state.arrowState) {
				case ArrowState.PeekLeftAndRight:
					leftArrowProps.visible = rightArrowProps.visible = true;
					break;
				case ArrowState.PeekLeft:
					leftArrowProps.visible = true;
					break;
				case ArrowState.PeekRight:
					rightArrowProps.visible = true;
					break;
			}
		} else {
			leftArrowProps.display = rightArrowProps.display = false;
		}

		let mainComponent = null;
		if (props.error) {
			mainComponent = <SliderError reload={this.props.reload} />;
		} else if (isLoading) {
			mainComponent = <SliderLoading />;
		} else if (noRecommendationsFound) {
			mainComponent = (
				<SliderError
					message={`Hmm, we can’t find recommendations for this ${
						props.data.kind === RecommendationKind.QnA ? "subject" : "book"
					}.`}
				/>
			);
		} else {
			mainComponent = props.data.set.items.map(
				(recommendation: RecommendationItem, index) => (
					<GenericCard
						key={index}
						{...recommendation}
						click={(cardData: CardType) => this.handleCardClick(cardData)}
						cardNumber={index + 1}
					/>
				)
			);
		}

		return (
			<div ref={this.sliderRootRef} className="csrec-slider-control">
				<Arrow {...leftArrowProps} />
				<Arrow {...rightArrowProps} />
				<div className="csrec-slider-viewport">
					<div
						className={`${
							noRecommendationsFound ? "csrec-no-recommendations " : ""
						} ${
							this.props.error ? "csrec-slider-error " : ""
						} csrec-slider-main csrec-main`}
						style={{ transform: `translateX(${state.translateX}px)` }}
					>
						{mainComponent}
					</div>
				</div>
			</div>
		);
	}

	componentDidUpdate(prevProps: SliderProps) {
		const didRenderedWithCards = this.isDataAvailable(prevProps.data);
		const renderedWithCards = this.isDataAvailable(this.props.data);
		if (didRenderedWithCards && renderedWithCards) {
			const dataUnchanged =
				prevProps.data.set.items.length === this.props.data.set.items.length &&
				prevProps.data.set.items.some((item, index) => {
					if (prevProps.data.kind === RecommendationKind.QnA) {
						let prevQnaItem = item as QnARecommendation;
						let qnaItem = this.props.data.set.items[index] as QnARecommendation;
						return qnaItem.questionId === prevQnaItem.questionId;
					} else {
						let prevQnaItem = item as TBSRecommendation;
						let qnaItem = this.props.data.set.items[index] as TBSRecommendation;
						return qnaItem.isbn13 === prevQnaItem.isbn13;
					}
				});
			if (dataUnchanged) return;
		}

		if (
			(prevProps.error && !this.props.error) ||
			(!didRenderedWithCards && renderedWithCards)
		) {
			this.sliderHelper = new SliderHelper(this.sliderRootRef);
			this.sliderHelper.resetScroll();
			this.sliderHelper.updateSliderDimensions();
			this.updateArrowState();
		}
		if (
			(!prevProps.error && this.props.error) ||
			(didRenderedWithCards && !renderedWithCards)
		) {
			this.sliderHelper.translateX = 0;
			this.sliderHelper.resetScroll();
			this.updateArrowState();
		}
		if (this.props.data && !prevProps.data) {
			this.sliderHelper.setSliderMode();
		}
		if (renderedWithCards) {
			logRecommendationAvailability(this.props.data.kind);
			this.sliderHelper.translateX = 0;
			this.sliderHelper.resetScroll();
			this.sliderHelper.updateSliderDimensions();
			this.updateArrowState();
		}
	}

	isDataAvailable(data: RecommendationList) {
		return data && data.set && data.set.items && data.set.items.length > 0;
	}

	componentDidMount() {
		if (this.props.error) {
			return;
		}
		this.sliderHelper = new SliderHelper(this.sliderRootRef);
		if (this.sliderHelper.sliderViewport) {
			this.sliderHelper.sliderViewport.addEventListener(
				"scroll",
				this._debouncedScroll
			);
		}
		window.addEventListener("resize", this._debouncedResize);
	}

	async handleCardClick(cardData: CardType) {
		const hasVideo = !!cardData.thumbnailURL;
		await doCardLogging(cardData, this.props.data.kind, this.props.data.set.id);
		const isQnA = this.props.data.kind === RecommendationKind.QnA;
		if (typeof window !== "undefined" && hasVideo && isQnA) {
			window.localStorage.setItem(`qna_video_card_clicked`, "1");
		}
		window.location.href = cardData.link;
	}

	updateArrowState(): void {
		this.sliderHelper.setArrowState();
		if (this.sliderHelper.sliderMode !== this.state.sliderMode) {
			this.sliderHelper.translateX = 0;
			this.sliderHelper.resetScroll();
		}
		if (
			this.sliderHelper.arrowState === this.state.arrowState &&
			this.sliderHelper.translateX === this.state.translateX &&
			this.sliderHelper.sliderMode === this.state.sliderMode
		) {
			return;
		}
		this.setState({
			arrowState: this.sliderHelper.arrowState,
			translateX: this.sliderHelper.translateX,
			sliderMode: this.sliderHelper.sliderMode,
		});
	}

	scroll(e: Event): void {
		this.sliderHelper.setArrowState();
		if (this.sliderHelper.arrowState === this.state.arrowState) {
			return;
		}
		this.setState({
			arrowState: this.sliderHelper.arrowState,
		});
	}

	resize(e: Event): void {
		this.sliderHelper.setSliderMode();
		this.sliderHelper.updateViewportDimensions();
		this.sliderHelper.updateSliderDimensions();
		this.sliderHelper.anchorSliderPositionToEndIfNeeded();
		this.updateArrowState();
	}

	moveLeft(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
		const arrowState = this.sliderHelper.moveLeft(e);
		this.setState({
			arrowState: arrowState,
			translateX: this.sliderHelper.translateX,
		});
	}

	moveRight(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
		const arrowState = this.sliderHelper.moveRight(e);
		this.setState({
			arrowState: arrowState,
			translateX: this.sliderHelper.translateX,
		});
	}
}

export default Slider;
